import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import { Input, Text, Spinner } from "@fluentui/react-components";
import { PersonRegular, DismissCircleRegular } from "@fluentui/react-icons";
const GRAPH_BASE = "https://graph.microsoft.com/beta";
export const PeoplePicker = ({ label, required, selectedName, selectedEmail, onSelect, onClear, accessToken, placeholder, }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const debounceRef = useRef();
    const searchUsers = useCallback(async (search) => {
        if (!accessToken || search.length < 2) {
            setResults([]);
            return;
        }
        setLoading(true);
        try {
            // Appel Graph direct (pas via le backend — économise les appels Azure Function)
            const encoded = encodeURIComponent(search);
            const res = await fetch(`${GRAPH_BASE}/users?$filter=startswith(displayName,'${encoded}') or startswith(mail,'${encoded}') or startswith(givenName,'${encoded}') or startswith(surname,'${encoded}')&$top=10&$select=id,displayName,mail,givenName,surname,jobTitle,department`, { headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" } });
            if (!res.ok) {
                setResults([]);
                return;
            }
            const data = await res.json();
            setResults(data.value.filter((u) => u.mail));
        }
        catch {
            setResults([]);
        }
        finally {
            setLoading(false);
        }
    }, [accessToken]);
    const handleInputChange = (value) => {
        setQuery(value);
        setOpen(true);
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchUsers(value), 300);
    };
    const handleSelect = (user) => {
        onSelect(user);
        setQuery("");
        setResults([]);
        setOpen(false);
    };
    const handleClear = () => {
        onClear();
        setQuery("");
        setResults([]);
    };
    // Close dropdown on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);
    const isSelected = !!selectedEmail;
    return (_jsxs("div", { ref: containerRef, style: { position: "relative" }, children: [label && (_jsxs("label", { style: { display: "block", marginBottom: "4px", fontWeight: 600, fontSize: "14px" }, children: [label, required && _jsx("span", { style: { color: "#c50f1f" }, children: " *" })] })), isSelected ? (_jsxs("div", { style: {
                    display: "flex", alignItems: "center", gap: "8px",
                    padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: "4px",
                    backgroundColor: "#f0f4ff",
                }, children: [_jsx(PersonRegular, { style: { fontSize: "20px", color: "#0078d4" } }), _jsxs("div", { style: { flex: 1 }, children: [_jsx(Text, { weight: "semibold", size: 300, children: selectedName }), _jsx(Text, { size: 200, style: { display: "block", color: "#666" }, children: selectedEmail })] }), _jsx("button", { onClick: handleClear, style: { background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex" }, "aria-label": "Effacer la s\u00E9lection", children: _jsx(DismissCircleRegular, { style: { fontSize: "20px", color: "#999" } }) })] })) : (_jsx(Input, { value: query, onChange: (_, d) => handleInputChange(d.value), onFocus: () => query.length >= 2 && setOpen(true), placeholder: placeholder || "Rechercher dans l'annuaire...", contentBefore: _jsx(PersonRegular, {}), contentAfter: loading ? _jsx(Spinner, { size: "tiny" }) : undefined, style: { width: "100%" } })), open && results.length > 0 && (_jsx("div", { style: {
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000,
                    backgroundColor: "#fff", border: "1px solid #d1d5db", borderRadius: "4px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: "250px", overflowY: "auto",
                    marginTop: "2px",
                }, children: results.map((user) => (_jsxs("button", { onClick: () => handleSelect(user), style: {
                        display: "flex", alignItems: "center", gap: "10px",
                        width: "100%", padding: "10px 12px", border: "none",
                        backgroundColor: "transparent", cursor: "pointer", textAlign: "left",
                    }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = "#f5f5f5"), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor = "transparent"), children: [_jsx(PersonRegular, { style: { fontSize: "24px", color: "#0078d4", flexShrink: 0 } }), _jsxs("div", { style: { minWidth: 0 }, children: [_jsx(Text, { weight: "semibold", size: 300, style: { display: "block" }, children: user.displayName }), _jsx(Text, { size: 200, style: { display: "block", color: "#666" }, children: user.mail }), (user.jobTitle || user.department) && (_jsx(Text, { size: 200, style: { display: "block", color: "#999" }, children: [user.jobTitle, user.department].filter(Boolean).join(" - ") }))] })] }, user.id))) })), open && query.length >= 2 && !loading && results.length === 0 && (_jsx("div", { style: {
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000,
                    backgroundColor: "#fff", border: "1px solid #d1d5db", borderRadius: "4px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)", padding: "16px", textAlign: "center",
                    marginTop: "2px",
                }, children: _jsxs(Text, { size: 200, style: { color: "#999" }, children: ["Aucun r\u00E9sultat pour \"", query, "\""] }) }))] }));
};
